import type {
  ClassificationRequest,
  ConflictRequest,
  FieldCatalogue,
  FieldEditRequest,
  RequestDetail,
  RequestPanel,
  RequestSummary,
} from "@procuremate/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { api } from "@/lib/api-client";

export const requestKeys = {
  all: ["requests"] as const,
  list: ["requests", "list"] as const,
  detail: (id: string) => ["requests", "detail", id] as const,
  fields: ["fields"] as const,
};

export function useRequests() {
  return useQuery({ queryKey: requestKeys.list, queryFn: () => api<RequestSummary[]>("/requests") });
}

/** Creates a draft request in the UI language and opens its conversation. */
export function useCreateRequest() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (language: "it" | "en") => api<RequestDetail>("/requests", { body: { language } }),
    onSuccess: (detail) => {
      queryClient.setQueryData(requestKeys.detail(detail.id), detail);
      void queryClient.invalidateQueries({ queryKey: requestKeys.list });
      navigate(`/requests/${detail.id}`);
    },
  });
}

/** The request with its messages. `refetchInterval` keeps the panel fresh while a review may be running. */
/** While a document is being read the panel follows its status (ADR-0020). */
const DOCUMENT_POLL_MS = 2500;

export function useRequestDetail(id: string, options: { refetchInterval?: () => number | false } = {}) {
  return useQuery({
    queryKey: requestKeys.detail(id),
    queryFn: () => api<RequestDetail>(`/requests/${id}`),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const reading = query.state.data?.documents.some((d) => d.status === "PENDING" || d.status === "PROCESSING");
      return reading ? DOCUMENT_POLL_MS : (options.refetchInterval?.() ?? false);
    },
  });
}

/** The field registry (labels, types, options). It only changes with a deployment. */
export function useFieldCatalogue() {
  return useQuery({
    queryKey: requestKeys.fields,
    queryFn: () => api<FieldCatalogue>("/fields"),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** Writes a fresh panel (from `case.updated` or an edit) into the cached request, keeping its messages. */
export function useApplyPanel(id: string) {
  const queryClient = useQueryClient();
  return (panel: RequestPanel) =>
    queryClient.setQueryData<RequestDetail>(requestKeys.detail(id), (old) =>
      old ? { ...old, ...panel, messages: old.messages } : old,
    );
}

/** POST /api/requests/:id/reopen — an abandoned draft becomes a draft again (ADR-0024). */
export function useReopenRequest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<RequestDetail>(`/requests/${id}/reopen`, { method: "POST" }),
    onSuccess: (detail) => {
      queryClient.setQueryData(requestKeys.detail(id), detail);
      void queryClient.invalidateQueries({ queryKey: requestKeys.list });
    },
  });
}

/** PUT /api/requests/:id/classification — the requester sets the classification (WP-2.6). */
export function useSetClassification(id: string) {
  const applyPanel = useApplyPanel(id);
  return useMutation({
    mutationFn: (body: ClassificationRequest) =>
      api<RequestPanel>(`/requests/${id}/classification`, { method: "PUT", body }),
    onSuccess: applyPanel,
  });
}

/** POST /api/requests/:id/submit (ADR-0014): the server's gate decides; 409 carries the blockers. */
export function useSubmitRequest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (anyway: boolean) => api<RequestDetail>(`/requests/${id}/submit`, { body: { anyway } }),
    onSuccess: (detail) => {
      queryClient.setQueryData(requestKeys.detail(id), detail);
      void queryClient.invalidateQueries({ queryKey: requestKeys.list });
    },
    // A refusal means the request changed under us (another tab, a turn): show the current state.
    onError: () => queryClient.invalidateQueries({ queryKey: requestKeys.detail(id) }),
  });
}

/** PATCH /api/requests/:id/fields/:key — an inline edit in the live panel (ADR-0014). */
export function useEditField(id: string) {
  const applyPanel = useApplyPanel(id);
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: FieldEditRequest["value"] }) =>
      api<RequestPanel>(`/requests/${id}/fields/${key}`, { method: "PATCH", body: { value } }),
    onSuccess: applyPanel,
  });
}

/** PUT /api/requests/:id/findings/:findingId — "Before you send": leave a point to procurement, or take it back (WP-2.8). */
export function useLeaveFinding(id: string) {
  const applyPanel = useApplyPanel(id);
  return useMutation({
    mutationFn: ({ findingId, leftToBuyer }: { findingId: string; leftToBuyer: boolean }) =>
      api<RequestPanel>(`/requests/${id}/findings/${findingId}`, { method: "PUT", body: { leftToBuyer } }),
    onSuccess: applyPanel,
  });
}

/** PUT /api/requests/:id/fields/:key/conflict — keep the requester's value or take the document's (WP-2.8). */
export function useResolveConflict(id: string) {
  const applyPanel = useApplyPanel(id);
  return useMutation({
    mutationFn: ({ key, keep }: { key: string; keep: ConflictRequest["keep"] }) =>
      api<RequestPanel>(`/requests/${id}/fields/${key}/conflict`, { method: "PUT", body: { keep } }),
    onSuccess: applyPanel,
  });
}
